import type { Spell, SpellSchool } from '@character-creator/types';
import { cantrips } from './cantrips';
import { level1Spells } from './level-1';
import { level2Spells } from './level-2';
import { level3Spells } from './level-3';
import { level4Spells } from './level-4';
import { level5Spells } from './level-5';
import { level6Spells } from './level-6';
import { level7Spells } from './level-7';
import { level8Spells } from './level-8';
import { level9Spells } from './level-9';

// Combine all spells into a single array
export const spells: Spell[] = [
  ...cantrips,
  ...level1Spells,
  ...level2Spells,
  ...level3Spells,
  ...level4Spells,
  ...level5Spells,
  ...level6Spells,
  ...level7Spells,
  ...level8Spells,
  ...level9Spells,
];

// Create a lookup map for efficient spell retrieval by ID
export const spellMap: Map<string, Spell> = new Map(
  spells.map((spell) => [spell.id, spell])
);

// Helper function to get a spell by ID
export function getSpell(id: string): Spell | undefined {
  return spellMap.get(id);
}

// Get all spells, optionally filtered by source
export function getSpells(source?: string): Spell[] {
  return source ? spells.filter((spell) => spell.source === source) : spells;
}

// Helper function to get all spells for a specific class
export function getSpellsByClass(classId: string, source?: string): Spell[] {
  const filtered = spells.filter((spell) => spell.classes.includes(classId));
  return source ? filtered.filter((spell) => spell.source === source) : filtered;
}

// Helper function to get all spells of a specific level
export function getSpellsByLevel(level: number, source?: string): Spell[] {
  const filtered = spells.filter((spell) => spell.level === level);
  return source ? filtered.filter((spell) => spell.source === source) : filtered;
}

// Helper function to get all spells of a specific school
export function getSpellsBySchool(school: SpellSchool, source?: string): Spell[] {
  const filtered = spells.filter((spell) => spell.school === school);
  return source ? filtered.filter((spell) => spell.source === source) : filtered;
}

// Helper function to get all ritual spells
export function getRitualSpells(source?: string): Spell[] {
  const filtered = spells.filter((spell) => spell.ritual);
  return source ? filtered.filter((spell) => spell.source === source) : filtered;
}

// Helper function to get all concentration spells
export function getConcentrationSpells(source?: string): Spell[] {
  const filtered = spells.filter((spell) => spell.concentration);
  return source ? filtered.filter((spell) => spell.source === source) : filtered;
}

// Helper function to get cantrips for a specific class
export function getCantripsForClass(classId: string, source?: string): Spell[] {
  const filtered = cantrips.filter((spell) => spell.classes.includes(classId));
  return source ? filtered.filter((spell) => spell.source === source) : filtered;
}

// Helper function to get spells for a class at a specific level
export function getSpellsForClassAtLevel(classId: string, level: number, source?: string): Spell[] {
  const filtered = spells.filter(
    (spell) => spell.classes.includes(classId) && spell.level === level
  );
  return source ? filtered.filter((spell) => spell.source === source) : filtered;
}

// Export individual level arrays for direct access if needed
export {
  cantrips,
  level1Spells,
  level2Spells,
  level3Spells,
  level4Spells,
  level5Spells,
  level6Spells,
  level7Spells,
  level8Spells,
  level9Spells,
};
